import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import './App.css';
import FeedBuilder from './components/FeedBuilder';
import { assetUrl } from './config';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Admin Panel Component with verbose logging
const AdminPanel = ({ isOpen, onClose, onFeedsChanged }) => {
  const [password, setPassword] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [status, setStatus] = useState(null);
  const [logs, setLogs] = useState(null);
  const [activeTab, setActiveTab] = useState('status');
  const [actionResult, setActionResult] = useState(null);
  const [verboseLogs, setVerboseLogs] = useState([]);
  const [showVerboseLog, setShowVerboseLog] = useState(false);
  const [installPath, setInstallPath] = useState('/opt/sports');
  const [pathSaved, setPathSaved] = useState(false);
  const logEndRef = useRef(null);

  const scrollToBottom = () => {
    logEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [verboseLogs]);

  // Fetch install path config on auth
  const fetchConfig = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/config`);
      if (res.ok) {
        const data = await res.json();
        setInstallPath(data.install_path || '/opt/sports');
      }
    } catch {}
  };

  const saveInstallPath = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/config`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password, install_path: installPath })
      });
      if (res.ok) {
        setPathSaved(true);
        setTimeout(() => setPathSaved(false), 2000);
      }
    } catch {}
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password })
      });
      if (res.ok) {
        setIsAuthenticated(true);
        fetchStatus();
        fetchConfig();
      } else {
        setError('Invalid password');
      }
    } catch {
      setError('Connection failed');
    } finally {
      setLoading(false);
    }
  };

  const fetchStatus = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/status`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password })
      });
      if (res.ok) setStatus(await res.json());
    } catch {}
  };

  const fetchLogs = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/logs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password })
      });
      if (res.ok) {
        const data = await res.json();
        setLogs(data.logs);
      }
    } catch {}
  };

  const handleVerboseAction = async (endpoint, actionType) => {
    setLoading(true);
    setActionResult(null);
    setVerboseLogs([]);
    setShowVerboseLog(true);
    
    // Add initial log entry
    setVerboseLogs([{ time: new Date().toISOString(), step: 'start', message: `Starting ${actionType}...` }]);
    
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password, install_path: installPath })
      });
      const data = await res.json();
      
      // If the response has verbose logs, display them
      if (data.logs && Array.isArray(data.logs)) {
        setVerboseLogs(prev => [...prev, ...data.logs]);
      }
      
      setActionResult({ type: actionType, ...data });
      fetchStatus();
    } catch (e) {
      setVerboseLogs(prev => [...prev, { time: new Date().toISOString(), step: 'error', message: `${actionType} failed: ${e.message}`, error: true }]);
      setActionResult({ type: actionType, success: false, message: `${actionType} failed` });
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async (endpoint, actionType) => {
    setLoading(true);
    setActionResult(null);
    setShowVerboseLog(false);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password })
      });
      const data = await res.json();
      setActionResult({ type: actionType, ...data });
      if (endpoint !== 'restart') fetchStatus();
    } catch (e) {
      setActionResult({ type: actionType, success: false, message: `${actionType} failed` });
    } finally {
      setLoading(false);
    }
  };

  const clearVerboseLogs = () => {
    setVerboseLogs([]);
    setShowVerboseLog(false);
  };

  useEffect(() => {
    if (activeTab === 'logs' && isAuthenticated) fetchLogs();
  }, [activeTab, isAuthenticated]);

  // Fetch config when panel opens to get correct install path
  useEffect(() => {
    if (isOpen) fetchConfig();
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
      <div className="bg-slate-800 rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden shadow-2xl border border-slate-700">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-700">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center">
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </div>
            <h2 className="text-white font-bold text-lg">Admin Panel</h2>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-700 text-slate-400 hover:text-white">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-80px)]">
          {!isAuthenticated ? (
            <form onSubmit={handleLogin} className="max-w-sm mx-auto">
              <div className="text-center mb-6">
                <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-slate-700 flex items-center justify-center">
                  <svg className="w-8 h-8 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </div>
                <h3 className="text-white font-semibold">Admin Authentication</h3>
              </div>
              {error && <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm">{error}</div>}
              <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Admin Password" className="w-full px-4 py-3 rounded-xl bg-slate-700 border border-slate-600 text-white placeholder-slate-400 focus:outline-none focus:border-amber-500" data-testid="admin-password-input" />
              <button type="submit" disabled={loading || !password} className="w-full mt-4 px-4 py-3 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 text-white font-semibold disabled:opacity-50" data-testid="admin-login-button">{loading ? 'Authenticating...' : 'Login'}</button>
            </form>
          ) : (
            <div>
              <div className="flex gap-2 mb-6 border-b border-slate-700 pb-4">
                {[['status', 'Status'], ['actions', 'Actions'], ['feeds', 'Feeds & Logos'], ['logs', 'Logs']].map(([tab, label]) => (
                  <button key={tab} onClick={() => setActiveTab(tab)} className={`px-4 py-2 rounded-lg font-medium text-sm ${activeTab === tab ? 'bg-amber-500 text-white' : 'bg-slate-700 text-slate-300 hover:bg-slate-600'}`} data-testid={`admin-tab-${tab}`}>{label}</button>
                ))}
              </div>
              
              {/* Action Result Banner */}
              {actionResult && !showVerboseLog && (
                <div className={`mb-4 p-4 rounded-lg border ${actionResult.success ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400' : 'bg-red-500/10 border-red-500/20 text-red-400'}`}>
                  <div className="font-medium">{actionResult.success ? '✓ Success' : '✗ Failed'}</div>
                  <div className="text-sm mt-1 opacity-80">{actionResult.message}</div>
                </div>
              )}
              
              {/* Verbose Log Window */}
              {showVerboseLog && (
                <div className="mb-4 bg-slate-900 rounded-xl border border-slate-700 overflow-hidden">
                  <div className="flex items-center justify-between px-4 py-2 bg-slate-800 border-b border-slate-700">
                    <div className="flex items-center gap-2">
                      <div className={`w-2 h-2 rounded-full ${loading ? 'bg-amber-400 animate-pulse' : actionResult?.success ? 'bg-emerald-400' : 'bg-red-400'}`}></div>
                      <span className="text-white text-sm font-medium">Update Log</span>
                    </div>
                    <button onClick={clearVerboseLogs} className="text-slate-400 hover:text-white text-xs">Clear</button>
                  </div>
                  <div className="p-4 max-h-64 overflow-y-auto font-mono text-xs">
                    {verboseLogs.map((log, index) => (
                      <div key={index} className={`py-1 ${log.error ? 'text-red-400' : 'text-slate-300'}`}>
                        <span className="text-slate-500">[{new Date(log.time).toLocaleTimeString()}]</span>
                        <span className={`ml-2 px-1.5 py-0.5 rounded text-xs ${
                          log.step === 'complete' ? 'bg-emerald-500/20 text-emerald-400' :
                          log.step === 'error' ? 'bg-red-500/20 text-red-400' :
                          'bg-blue-500/20 text-blue-400'
                        }`}>{log.step}</span>
                        <span className="ml-2 whitespace-pre-wrap">{log.message}</span>
                        {log.output && <pre className="mt-1 ml-4 text-slate-500 whitespace-pre-wrap">{log.output}</pre>}
                      </div>
                    ))}
                    <div ref={logEndRef} />
                  </div>
                  {actionResult && (
                    <div className={`px-4 py-2 border-t border-slate-700 ${actionResult.success ? 'bg-emerald-500/10 text-emerald-400' : 'bg-red-500/10 text-red-400'}`}>
                      <span className="text-sm font-medium">{actionResult.success ? '✓ ' : '✗ '}{actionResult.message}</span>
                    </div>
                  )}
                </div>
              )}
              
              {activeTab === 'status' && status && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-slate-700/50 rounded-xl p-4"><h4 className="text-white font-semibold mb-3">Application</h4><div className="space-y-2 text-sm">{[['Name', status.app?.name || 'SportsPulse'], ['Version', status.app?.version || 'N/A'], ['Platform', status.system.platform], ['Python', status.system.python_version]].map(([k, v]) => <div key={k} className="flex justify-between"><span className="text-slate-400">{k}</span><span className={k === 'Version' ? 'text-emerald-400 font-mono' : 'text-white'}>{v}</span></div>)}</div></div>
                  <div className="bg-slate-700/50 rounded-xl p-4"><h4 className="text-white font-semibold mb-3">System</h4><div className="space-y-2 text-sm">{[['CPU', `${status.system.cpu_percent}%`], ['Memory', `${status.system.memory_percent}%`], ['Disk', `${status.system.disk_percent}%`]].map(([k, v]) => <div key={k} className="flex justify-between"><span className="text-slate-400">{k}</span><span className="text-white">{v}</span></div>)}</div></div>
                  <div className="bg-slate-700/50 rounded-xl p-4"><h4 className="text-white font-semibold mb-3">Git</h4><div className="space-y-2 text-sm">{[['Branch', status.git.branch], ['Commit', status.git.commit], ['Status', status.git.status]].map(([k, v]) => <div key={k} className="flex justify-between"><span className="text-slate-400">{k}</span><span className="text-white">{v}</span></div>)}</div></div>
                  <div className="bg-slate-700/50 rounded-xl p-4"><h4 className="text-white font-semibold mb-3">Services</h4><div className="space-y-2 text-sm">{Object.entries(status.services).map(([k, v]) => <div key={k} className="flex justify-between"><span className="text-slate-400">{k}</span><span className={v === 'RUNNING' ? 'text-emerald-400' : 'text-red-400'}>{v}</span></div>)}</div></div>
                  <div className="bg-slate-700/50 rounded-xl p-4"><h4 className="text-white font-semibold mb-3">Cache</h4><div className="space-y-2 text-sm">{[['Status', status.redis.available ? 'Connected' : 'Disconnected'], ['Hits', status.cache_stats.hits], ['Misses', status.cache_stats.misses]].map(([k, v]) => <div key={k} className="flex justify-between"><span className="text-slate-400">{k}</span><span className="text-white">{v}</span></div>)}</div></div>
                </div>
              )}
              {activeTab === 'feeds' && (
                <FeedBuilder password={password} onFeedsChanged={onFeedsChanged} />
              )}
              {activeTab === 'actions' && (
                <div className="space-y-6">
                  {/* Install Path Configuration */}
                  <div className="bg-slate-700/30 rounded-xl p-4 border border-slate-600/50">
                    <label className="text-slate-300 text-sm font-medium mb-2 block">Install Path</label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={installPath}
                        onChange={(e) => setInstallPath(e.target.value)}
                        placeholder="/opt/sports"
                        className="flex-1 px-3 py-2 rounded-lg bg-slate-800 border border-slate-600 text-white text-sm placeholder-slate-500 focus:outline-none focus:border-blue-500"
                        data-testid="install-path-input"
                      />
                      <button
                        onClick={saveInstallPath}
                        className={`px-4 py-2 rounded-lg text-white text-sm font-medium transition-colors ${pathSaved ? 'bg-emerald-500' : 'bg-slate-600 hover:bg-slate-500'}`}
                        data-testid="save-path-btn"
                      >
                        {pathSaved ? '✓ Saved' : 'Save'}
                      </button>
                    </div>
                    <p className="text-slate-500 text-xs mt-2">Full path to your SportsPulse installation directory</p>
                  </div>

                  {/* Update Section */}
                  <div className="bg-slate-700/30 rounded-xl p-5 border border-slate-600/50">
                    <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                      <svg className="w-5 h-5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                      </svg>
                      Software Update
                    </h3>
                    <p className="text-slate-400 text-sm mb-4">Pull latest code from GitHub and install dependencies</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <button 
                        onClick={() => handleVerboseAction('pull', 'Pull Updates')} 
                        disabled={loading}
                        className="px-4 py-3 rounded-lg bg-blue-500 hover:bg-blue-600 text-white font-medium disabled:opacity-50 flex items-center justify-center gap-2"
                        data-testid="pull-updates-btn"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                        </svg>
                        {loading ? 'Pulling...' : 'Pull Updates'}
                      </button>
                      <button 
                        onClick={() => handleVerboseAction('install', 'Install Dependencies')} 
                        disabled={loading}
                        className="px-4 py-3 rounded-lg bg-purple-500 hover:bg-purple-600 text-white font-medium disabled:opacity-50 flex items-center justify-center gap-2"
                        data-testid="install-deps-btn"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
                        </svg>
                        {loading ? 'Installing...' : 'Install Dependencies'}
                      </button>
                    </div>
                  </div>
                  
                  {/* Other Actions */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-slate-700/50 rounded-xl p-5">
                      <h4 className="text-white font-semibold mb-2 flex items-center gap-2">
                        <svg className="w-4 h-4 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                        Restart Services
                      </h4>
                      <p className="text-slate-400 text-sm mb-4">Restart all application services</p>
                      <button 
                        onClick={() => handleAction('restart', 'Restart Services')} 
                        disabled={loading}
                        className="w-full px-4 py-2 rounded-lg bg-amber-500 hover:bg-amber-600 text-white font-medium disabled:opacity-50"
                        data-testid="restart-services-btn"
                      >
                        {loading ? 'Restarting...' : 'Restart Services'}
                      </button>
                    </div>
                    <div className="bg-slate-700/50 rounded-xl p-5">
                      <h4 className="text-white font-semibold mb-2 flex items-center gap-2">
                        <svg className="w-4 h-4 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                        Clear Cache
                      </h4>
                      <p className="text-slate-400 text-sm mb-4">Flush all cached API data</p>
                      <button 
                        onClick={() => handleAction('cache/clear', 'Clear Cache')} 
                        disabled={loading}
                        className="w-full px-4 py-2 rounded-lg bg-red-500 hover:bg-red-600 text-white font-medium disabled:opacity-50"
                        data-testid="clear-cache-btn"
                      >
                        {loading ? 'Clearing...' : 'Clear Cache'}
                      </button>
                    </div>
                  </div>
                </div>
              )}
              {activeTab === 'logs' && logs && <div className="space-y-4">{Object.entries(logs).map(([name, content]) => <div key={name} className="bg-slate-700/50 rounded-xl overflow-hidden"><div className="px-4 py-2 bg-slate-700"><h4 className="text-white font-medium text-sm">{name.replace('_', ' ').toUpperCase()}</h4></div><pre className="p-4 text-xs text-slate-300 overflow-x-auto max-h-48 font-mono">{content || 'No logs'}</pre></div>)}</div>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Expandable Broadcast Component
const BroadcastInfo = ({ broadcasts }) => {
  const [expanded, setExpanded] = useState(false);
  
  if (!broadcasts || broadcasts.length === 0) return null;
  
  const uniqueChannels = [...new Set(broadcasts.map(b => b.strChannel))].filter(Boolean);
  if (uniqueChannels.length === 0) return null;

  // Channels flagged as streaming feeds (e.g. "NFL Pass 1") get an accent chip
  const feedChannels = new Set(broadcasts.filter(b => b.isFeed).map(b => b.strChannel));

  const displayCount = expanded ? uniqueChannels.length : 3;
  const hasMore = uniqueChannels.length > 3;

  return (
    <div className="mt-4 pt-4 border-t border-slate-700/50">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <svg className="w-4 h-4 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
          </svg>
          <span className="text-slate-500 text-xs font-medium uppercase tracking-wider">Watch On</span>
        </div>
        {hasMore && (
          <button 
            onClick={() => setExpanded(!expanded)}
            className="text-xs text-emerald-400 hover:text-emerald-300 font-medium flex items-center gap-1"
          >
            {expanded ? 'Show less' : `+${uniqueChannels.length - 3} more`}
            <svg className={`w-3 h-3 transition-transform ${expanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        )}
      </div>
      <div className={`flex flex-wrap gap-1.5 ${expanded ? '' : 'max-h-8 overflow-hidden'}`}>
        {uniqueChannels.slice(0, displayCount).map((channel, idx) => (
          <span
            key={idx}
            className={`px-2 py-0.5 rounded text-xs font-medium ${
              feedChannels.has(channel)
                ? 'bg-emerald-500/15 text-emerald-300 border border-emerald-500/30'
                : 'bg-slate-700/50 text-slate-300'
            }`}
          >
            {channel}
          </span>
        ))}
      </div>
    </div>
  );
};

// Team Logo
const TeamLogo = ({ team, teamBadge, size = 'md' }) => {
  const [imgError, setImgError] = useState(false);
  const sizeClasses = { sm: 'w-10 h-10', md: 'w-14 h-14', lg: 'w-18 h-18' };

  if (teamBadge && !imgError) {
    return <img src={teamBadge + '/small'} alt={team || 'Team'} className={`${sizeClasses[size]} object-contain`} onError={() => setImgError(true)} />;
  }
  const initials = team ? team.split(' ').map(w => w[0]).join('').slice(0, 3).toUpperCase() : '?';
  return <div className={`${sizeClasses[size]} rounded-lg bg-slate-700 flex items-center justify-center font-bold text-slate-300 text-sm`}>{initials}</div>;
};

// Live Indicator
const LiveIndicator = () => (
  <div className="flex items-center gap-1.5 px-2 py-1 bg-red-500/10 rounded-full">
    <span className="relative flex h-2 w-2">
      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
      <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500"></span>
    </span>
    <span className="text-red-500 font-semibold text-xs uppercase tracking-wider">Live</span>
  </div>
);

// Event Card
const EventCard = ({ event, isLive, broadcasts }) => {
  const formatTime = (time, dateStr) => {
    if (!time || time === '00:00:00') return '';
    try {
      // Combine date and time, treat as UTC, convert to user's local timezone
      const utcDateTime = new Date(`${dateStr}T${time}Z`);
      const timeStr = utcDateTime.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
      // Get the user's timezone abbreviation
      const tzAbbr = utcDateTime.toLocaleTimeString('en-US', {
        timeZoneName: 'short'
      }).split(' ').pop();
      return `${timeStr} ${tzAbbr}`;
    } catch { return time; }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      // Use noon UTC to avoid date shifting across timezones
      const date = new Date(dateStr + 'T12:00:00Z');
      return date.toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric'
      });
    } catch { return dateStr; }
  };

  const homeScore = event.intHomeScore ?? event.strHomeScore ?? null;
  const awayScore = event.intAwayScore ?? event.strAwayScore ?? null;
  const hasScore = homeScore !== null && awayScore !== null && (homeScore !== '' || awayScore !== '');
  const status = event.strStatus || event.strProgress || '';

  return (
    <div data-testid={`event-card-${event.idEvent}`} className={`bg-slate-800/50 backdrop-blur rounded-xl border transition-all duration-300 hover:shadow-xl ${isLive ? 'border-red-500/30 shadow-lg shadow-red-500/5' : 'border-slate-700/50 hover:border-slate-600/50'}`}>
      <div className="p-5">
        <div className="flex justify-between items-center mb-5">
          <div className="flex items-center gap-2 text-slate-400 text-sm">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
            <span>{formatDate(event.dateEvent)}</span>
            {event.strTime && event.strTime !== '00:00:00' && <><span className="text-slate-600">•</span><span>{formatTime(event.strTime, event.dateEvent)}</span></>}
          </div>
          {isLive ? <LiveIndicator /> : status && status !== 'NS' && <span className="text-xs px-2 py-1 bg-slate-700/50 text-slate-400 rounded-full font-medium">{status}</span>}
        </div>
        <div className="flex items-center justify-between">
          <div className="flex-1 flex flex-col items-center gap-2">
            <TeamLogo team={event.strHomeTeam} teamBadge={event.strHomeTeamBadge} />
            <span className="text-white text-sm font-medium text-center leading-tight">{event.strHomeTeam}</span>
          </div>
          <div className="px-4 flex flex-col items-center">
            {hasScore ? (
              <div className="flex items-center gap-3">
                <span className={`text-2xl font-bold ${isLive ? 'text-emerald-400' : 'text-white'}`}>{homeScore}</span>
                <span className="text-slate-600 font-light">-</span>
                <span className={`text-2xl font-bold ${isLive ? 'text-emerald-400' : 'text-white'}`}>{awayScore}</span>
              </div>
            ) : <span className="text-slate-500 font-medium text-lg">VS</span>}
            {isLive && status && <span className="text-red-400 text-xs mt-1 font-medium">{status}</span>}
          </div>
          <div className="flex-1 flex flex-col items-center gap-2">
            <TeamLogo team={event.strAwayTeam} teamBadge={event.strAwayTeamBadge} />
            <span className="text-white text-sm font-medium text-center leading-tight">{event.strAwayTeam}</span>
          </div>
        </div>
        {event.strVenue && (
          <div className="mt-4 flex items-center justify-center gap-1.5 text-slate-500 text-xs">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
            <span>{event.strVenue}</span>
          </div>
        )}
        <BroadcastInfo broadcasts={broadcasts} />
      </div>
    </div>
  );
};

// Loading Spinner
const LoadingSpinner = () => (
  <div className="flex items-center justify-center py-16">
    <div className="relative">
      <div className="w-12 h-12 rounded-full border-2 border-slate-700"></div>
      <div className="absolute top-0 left-0 w-12 h-12 rounded-full border-2 border-transparent border-t-emerald-500 animate-spin"></div>
    </div>
  </div>
);

// Empty State
const EmptyState = ({ leagueName, isSearch }) => (
  <div className="text-center py-16">
    <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-slate-800 flex items-center justify-center">
      <svg className="w-8 h-8 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d={isSearch ? "M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" : "M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"} />
      </svg>
    </div>
    <h3 className="text-lg font-semibold text-white mb-1">{isSearch ? 'No Results Found' : 'No Upcoming Events'}</h3>
    <p className="text-slate-500 text-sm">{isSearch ? 'Try a different search term' : `Check back later for ${leagueName} events`}</p>
  </div>
);

// Main App
function App() {
  const [sports, setSports] = useState([]);
  const [selectedSport, setSelectedSport] = useState(null);
  const [events, setEvents] = useState([]);
  const [liveScores, setLiveScores] = useState([]);
  const [broadcasts, setBroadcasts] = useState({});
  const [loading, setLoading] = useState(true);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showAdmin, setShowAdmin] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Fetch sports (also re-run after feeds are edited in the admin builder)
  const fetchSports = useCallback(async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/sports`);
      const data = await response.json();
      const list = data.sports || [];
      setSports(list);
      // Keep the open section's name/logo current if its feed was just edited
      setSelectedSport(prev => (prev ? list.find(s => s.key === prev.key) || prev : prev));
      setLoading(false);
    } catch (err) {
      setError('Failed to load sports');
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSports();
  }, [fetchSports]);

  // Fetch events
  const fetchEvents = useCallback(async (sportKey) => {
    setEventsLoading(true);
    setError(null);
    setSearchQuery('');
    try {
      const eventsRes = await fetch(`${BACKEND_URL}/api/events/${sportKey}`);
      const eventsData = await eventsRes.json();
      setEvents(eventsData.events || []);

      const liveRes = await fetch(`${BACKEND_URL}/api/livescores/${sportKey}`);
      const liveData = await liveRes.json();
      setLiveScores(liveData.livescores || []);

      const eventIds = (eventsData.events || []).slice(0, 15).map(e => e.idEvent);
      const broadcastResults = await Promise.all(
        eventIds.map(async (id) => {
          try {
            const res = await fetch(`${BACKEND_URL}/api/event/${id}/tv`);
            const data = await res.json();
            return { id, broadcasts: data.broadcasts || [] };
          } catch { return { id, broadcasts: [] }; }
        })
      );
      const broadcastMap = {};
      broadcastResults.forEach(b => { broadcastMap[b.id] = b.broadcasts; });

      // For streaming feeds (NFL Pass 1/2), surface the feed itself as the primary
      // channel alongside the real TV broadcasts for each game.
      (eventsData.events || []).forEach(e => {
        if (e.feedChannel) {
          const existing = broadcastMap[e.idEvent] || [];
          broadcastMap[e.idEvent] = [{ strChannel: e.feedChannel, strCountry: 'USA', isFeed: true }, ...existing];
        }
      });
      setBroadcasts(broadcastMap);
    } catch (err) {
      setError('Failed to load events');
    } finally {
      setEventsLoading(false);
    }
  }, []);

  const handleSportSelect = (sport) => {
    setSelectedSport(sport);
    fetchEvents(sport.key);
  };

  const isEventLive = (event) => {
    const liveIds = liveScores.map(ls => ls.idEvent);
    if (liveIds.includes(event.idEvent)) return true;
    const status = (event.strStatus || '').toLowerCase();
    return ['live', 'progress', 'ht', 'ft', 'q1', 'q2', 'q3', 'q4', 'p1', 'p2', 'p3', 'aot', 'ot'].some(s => status.includes(s));
  };

  const mergedEvents = events.map(event => {
    const liveData = liveScores.find(ls => ls.idEvent === event.idEvent);
    return liveData ? { ...event, ...liveData } : event;
  });

  // Filter and sort events
  const filteredEvents = useMemo(() => {
    let filtered = mergedEvents;
    
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(event => 
        (event.strHomeTeam || '').toLowerCase().includes(query) ||
        (event.strAwayTeam || '').toLowerCase().includes(query) ||
        (event.strEvent || '').toLowerCase().includes(query) ||
        (event.strVenue || '').toLowerCase().includes(query)
      );
    }
    
    return [...filtered].sort((a, b) => {
      const aLive = isEventLive(a);
      const bLive = isEventLive(b);
      if (aLive && !bLive) return -1;
      if (!aLive && bLive) return 1;
      return 0;
    });
  }, [mergedEvents, searchQuery]);

  // Auto-refresh
  useEffect(() => {
    if (!selectedSport) return;
    const interval = setInterval(async () => {
      try {
        const liveRes = await fetch(`${BACKEND_URL}/api/livescores/${selectedSport.key}`);
        const liveData = await liveRes.json();
        setLiveScores(liveData.livescores || []);
      } catch {}
    }, 30000);
    return () => clearInterval(interval);
  }, [selectedSport]);

  if (loading) {
    return <div className="min-h-screen bg-slate-900 flex items-center justify-center"><LoadingSpinner /></div>;
  }

  return (
    <div className="min-h-screen bg-slate-900">
      <AdminPanel isOpen={showAdmin} onClose={() => setShowAdmin(false)} onFeedsChanged={fetchSports} />

      {/* Header */}
      <header className="sticky top-0 z-40 bg-slate-900/95 backdrop-blur-sm border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <div>
                <h1 className="text-white font-bold text-lg tracking-tight" data-testid="app-title">SportsPulse</h1>
                <p className="text-slate-500 text-xs">Live Scores & Schedules</p>
              </div>
            </div>
            <button onClick={() => setShowAdmin(true)} className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-amber-500/10 hover:bg-amber-500/20 text-amber-400 text-sm font-medium border border-amber-500/20" data-testid="admin-button">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
              Admin
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {error && (
          <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 text-red-400 rounded-xl flex items-center gap-3">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
            <span className="text-sm">{error}</span>
          </div>
        )}

        {/* League Selector - New Design */}
        <section className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-slate-400 text-xs font-semibold uppercase tracking-wider">Leagues</h2>
            {selectedSport && (
              <button onClick={() => { setSelectedSport(null); setEvents([]); setSearchQuery(''); }} className="text-xs text-slate-500 hover:text-slate-300">
                Clear selection
              </button>
            )}
          </div>
          <div className="flex flex-wrap gap-2" data-testid="sports-list">
            {sports.map((sport) => {
              const isSelected = selectedSport?.key === sport.key;
              return (
                <button
                  key={sport.key}
                  data-testid={`sport-card-${sport.key}`}
                  onClick={() => handleSportSelect(sport)}
                  className={`group flex items-center gap-2.5 px-4 py-2.5 rounded-xl transition-all duration-200 ${
                    isSelected 
                      ? 'bg-emerald-500 text-white shadow-lg shadow-emerald-500/25' 
                      : 'bg-slate-800 text-slate-300 hover:bg-slate-700 hover:text-white'
                  }`}
                >
                  {sport.logo ? (
                    <img src={assetUrl(sport.logo)} alt="" className="h-5 w-auto object-contain" onError={(e) => e.target.style.display = 'none'} />
                  ) : (
                    <span className="w-5 h-5 rounded bg-slate-600 flex items-center justify-center text-xs font-bold">{sport.name?.substring(0, 2)}</span>
                  )}
                  <span className="font-medium text-sm">{sport.name}</span>
                </button>
              );
            })}
          </div>
        </section>

        {/* Events Section */}
        {selectedSport && (
          <section>
            {/* Search and Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
              <div className="flex items-center gap-3">
                {selectedSport.logo && <img src={assetUrl(selectedSport.logo)} alt="" className="h-8 w-auto" />}
                <div>
                  <h2 className="text-white text-xl font-semibold">{selectedSport.name}</h2>
                  <p className="text-slate-500 text-sm">{filteredEvents.length} {searchQuery ? 'results' : 'events'}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                {/* Search Input */}
                <div className="relative">
                  <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search teams, venues..."
                    className="w-64 pl-10 pr-4 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white placeholder-slate-500 text-sm focus:outline-none focus:border-emerald-500 transition-colors"
                    data-testid="search-input"
                  />
                  {searchQuery && (
                    <button 
                      onClick={() => setSearchQuery('')}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                </div>
                {/* Refresh Button */}
                <button
                  onClick={() => fetchEvents(selectedSport.key)}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 text-sm font-medium transition-colors"
                  data-testid="refresh-button"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  Refresh
                </button>
              </div>
            </div>

            {/* Events Grid */}
            {eventsLoading ? (
              <LoadingSpinner />
            ) : filteredEvents.length === 0 ? (
              <EmptyState leagueName={selectedSport.name} isSearch={!!searchQuery} />
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="events-grid">
                {filteredEvents.map((event) => (
                  <EventCard
                    key={event.idEvent}
                    event={event}
                    isLive={isEventLive(event)}
                    broadcasts={broadcasts[event.idEvent]}
                  />
                ))}
              </div>
            )}
          </section>
        )}

        {/* Welcome */}
        {!selectedSport && (
          <div className="text-center py-20">
            <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-emerald-400/20 to-teal-500/20 flex items-center justify-center">
              <svg className="w-10 h-10 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">Welcome to SportsPulse</h2>
            <p className="text-slate-500 max-w-md mx-auto mb-8">
              Select a league above to view live scores, upcoming events, and broadcast information.
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              {sports.slice(0, 5).map(sport => (
                <button
                  key={sport.key}
                  onClick={() => handleSportSelect(sport)}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white transition-colors"
                >
                  {sport.logo && <img src={assetUrl(sport.logo)} alt="" className="h-4 w-auto" />}
                  <span className="text-sm font-medium">{sport.name}</span>
                </button>
              ))}
            </div>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-800 mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-center text-slate-500 text-sm">
            <p>Live scores refresh every 30 seconds</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;
